import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import PasswordStrengthMeter from '../../components/ui/PasswordStrengthMeter';
import { useLanguage } from '../../contexts/LanguageContext';

const ResetPassword = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [sessionReady, setSessionReady] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [verifying, setVerifying] = useState(true);

  useEffect(() => {
    // Supabase fires PASSWORD_RECOVERY when user clicks a password reset link.
    // For invite links (inviteUserByEmail), Supabase fires SIGNED_IN with a session.
    // We handle both so the form shows for both flows.
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setSessionReady(true);
      }
      // Invite links sign the user in directly — show the set-password form
      if (event === 'SIGNED_IN' && session) {
        setSessionReady(true);
      }
    });

    const init = async () => {
      try {
        const searchParams = new URLSearchParams(window.location.search);
        const tokenHash = searchParams?.get('token_hash');
        const otpType = searchParams?.get('type') || 'recovery';

        if (tokenHash) {
          // Direct invite / reset link: verify the one-time token only now, on user click
          const { data, error: verifyError } = await supabase?.auth?.verifyOtp({
            token_hash: tokenHash,
            type: otpType,
          });
          // Remove the token from the address bar so a refresh doesn't reuse it
          window.history?.replaceState({}, document.title, window.location?.pathname);
          if (verifyError || !data?.session) {
            setError(verifyError?.message || '');
          } else {
            setSessionReady(true);
          }
          return;
        }

        // Legacy links that return errors in the URL hash (e.g. #error=access_denied&error_code=otp_expired)
        const hashParams = new URLSearchParams(window.location.hash?.replace(/^#/, ''));
        if (hashParams?.get('error')) {
          setError(hashParams?.get('error_description')?.replace(/\+/g, ' ') || '');
          window.history?.replaceState({}, document.title, window.location?.pathname);
          return;
        }

        // Also check if there's already an active session (user already authenticated via link)
        const { data: { session } } = await supabase?.auth?.getSession();
        if (session) setSessionReady(true);
      } finally {
        setVerifying(false);
      }
    };
    init();

    return () => subscription?.unsubscribe();
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (password?.length < 8) {
      setError(t('rp_errTooShort'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('rp_errNoMatch'));
      return;
    }

    setIsLoading(true);
    try {
      const { error: updateError } = await supabase?.auth?.updateUser({ password });
      if (updateError) {
        setError(updateError?.message || t('rp_errUpdateFailed'));
      } else {
        setSuccess(true);
        setTimeout(() => navigate('/login', { replace: true }), 3000);
      }
    } catch (err) {
      setError(t('rp_errUnexpected'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'linear-gradient(135deg, #111111 0%, #1a0000 50%, #111111 100%)' }}>
      {/* Yellow accent stripe at top */}
      <div className="fixed top-0 left-0 right-0 h-1" style={{ background: '#FFD700' }}></div>

      <div className="w-full max-w-md">
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-4 shadow-2xl" style={{ background: '#CC0000' }}>
            <Icon name="Wrench" size={38} color="white" />
          </div>
          <h1 className="text-4xl font-bold tracking-widest uppercase" style={{ color: '#FFFFFF', letterSpacing: '0.15em' }}>
            SERVICE<span style={{ color: '#FFD700' }}>JOCKEY</span>
          </h1>
          <div className="flex items-center justify-center gap-2 mt-2">
            <div className="h-px w-12" style={{ background: '#CC0000' }}></div>
            <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#FFD700' }}>Field Service Management</p>
            <div className="h-px w-12" style={{ background: '#CC0000' }}></div>
          </div>
        </div>

        {/* Card */}
        <div className="rounded-2xl shadow-2xl p-8" style={{ background: '#1A1A1A', border: '1px solid #333333' }}>
          {success ? (
            <div className="text-center py-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ background: 'rgba(34,197,94,0.15)', border: '1px solid #22c55e' }}>
                <Icon name="CheckCircle" size={32} color="#22c55e" />
              </div>
              <h2 className="text-xl font-semibold mb-2" style={{ color: '#FFFFFF' }}>{t('rp_passwordUpdated')}</h2>
              <p className="text-sm mb-4" style={{ color: '#999999' }}>
                {t('rp_passwordUpdatedDesc')}
              </p>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 mx-auto" style={{ borderColor: '#CC0000' }}></div>
            </div>
          ) : !sessionReady ? (
            <div className="text-center py-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ background: 'rgba(204,0,0,0.15)', border: '1px solid #CC0000' }}>
                <Icon name="LinkIcon" size={32} color="#CC0000" />
              </div>
              <h2 className="text-xl font-semibold mb-2" style={{ color: '#FFFFFF' }}>{verifying ? 'Verifying link…' : t('rp_invalidLink')}</h2>
              <p className="text-sm mb-6" style={{ color: '#999999' }}>
                {verifying ? '' : (error || t('rp_invalidLinkDesc'))}
              </p>
              <Link
                to="/forgot-password"
                className="inline-flex items-center gap-2 font-bold py-2.5 px-6 rounded-xl uppercase tracking-wider text-sm"
                style={{ background: '#CC0000', color: '#FFFFFF', boxShadow: '0 4px 15px rgba(204,0,0,0.4)' }}
              >
                <Icon name="Mail" size={16} color="white" />
                {t('rp_requestNewLink')}
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h2 className="text-xl font-semibold mb-1" style={{ color: '#FFFFFF' }}>{t('rp_setNewPassword')}</h2>
                <p className="text-sm" style={{ color: '#999999' }}>{t('rp_chooseStrong')}</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="rounded-lg p-3 flex items-center gap-2" style={{ background: 'rgba(204,0,0,0.15)', border: '1px solid #CC0000' }}>
                    <Icon name="AlertCircle" size={16} color="#FF4444" />
                    <p className="text-sm" style={{ color: '#FF4444' }}>{error}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#CCCCCC' }}>{t('rp_newPassword')}</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e?.target?.value)}
                      placeholder={t('rp_minChars')}
                      required
                      className="w-full px-4 py-2.5 rounded-lg text-sm transition-colors pr-10"
                      style={{
                        background: '#111111',
                        border: '1px solid #444444',
                        color: '#FFFFFF',
                        outline: 'none',
                      }}
                      onFocus={(e) => { e.target.style.borderColor = '#CC0000'; e.target.style.boxShadow = '0 0 0 2px rgba(204,0,0,0.25)'; }}
                      onBlur={(e) => { e.target.style.borderColor = '#444444'; e.target.style.boxShadow = 'none'; }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                      style={{ color: '#666666', background: 'none', border: 'none', cursor: 'pointer' }}
                    >
                      <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
                    </button>
                  </div>
                  <PasswordStrengthMeter password={password} darkTheme={true} />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#CCCCCC' }}>{t('rp_confirmNewPassword')}</label>
                  <div className="relative">
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e?.target?.value)}
                      placeholder={t('rp_reEnter')}
                      required
                      className="w-full px-4 py-2.5 rounded-lg text-sm transition-colors pr-10"
                      style={{
                        background: '#111111',
                        border: '1px solid #444444',
                        color: '#FFFFFF',
                        outline: 'none',
                      }}
                      onFocus={(e) => { e.target.style.borderColor = '#CC0000'; e.target.style.boxShadow = '0 0 0 2px rgba(204,0,0,0.25)'; }}
                      onBlur={(e) => { e.target.style.borderColor = '#444444'; e.target.style.boxShadow = 'none'; }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                      style={{ color: '#666666', background: 'none', border: 'none', cursor: 'pointer' }}
                    >
                      <Icon name={showConfirm ? 'EyeOff' : 'Eye'} size={16} />
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 font-bold py-3 px-4 rounded-xl transition-all duration-200 mt-2 uppercase tracking-wider text-sm"
                  style={{
                    background: isLoading ? '#991111' : '#CC0000',
                    color: '#FFFFFF',
                    boxShadow: '0 4px 15px rgba(204,0,0,0.4)',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                  }}
                  onMouseEnter={(e) => { if (!isLoading) { e.target.style.background = '#FF0000'; e.target.style.boxShadow = '0 6px 20px rgba(204,0,0,0.6)'; }}}
                  onMouseLeave={(e) => { if (!isLoading) { e.target.style.background = '#CC0000'; e.target.style.boxShadow = '0 4px 15px rgba(204,0,0,0.4)'; }}}
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>{t('rp_updating')}</span>
                    </>
                  ) : (
                    <>
                      <Icon name="Lock" size={18} color="white" />
                      <span>{t('rp_updatePassword')}</span>
                    </>
                  )}
                </button>
              </form>
            </>
          )}

          {!success && (
            <div className="mt-6 pt-5" style={{ borderTop: '1px solid #2A2A2A' }}>
              <Link
                to="/login"
                className="flex items-center justify-center gap-2 text-sm transition-colors"
                style={{ color: '#999999' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#FFFFFF'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#999999'}
              >
                <Icon name="ArrowLeft" size={16} />
                {t('rp_backToSignIn')}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
